import { Draft } from "./inboxDraft.types";

import { pickMyOption } from "./resolveMyDefaultSender.util";

import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";
import type { User } from "@/pages/Public/pages/Login/schema/login";

/** Email-only fields are cleared when a draft leaves the email channel. */
const clearedEmailFields = (type: SenderRecipientOption["type"]): Partial<Draft> =>
	type !== "email" ? { cc: [], bcc: [], showCcBcc: false, subject: undefined } : {};

/** A connection request only exists on LinkedIn — leaving the channel drops the flag. */
const inviteFlagFor = (draft: Draft, type: SenderRecipientOption["type"]): Partial<Draft> =>
	draft.isInviteNote && type !== "linkedin" ? { isInviteNote: undefined } : {};

/**
 * Sender update (no AI re-injection): the chosen sender applies to EVERY draft —
 * the whole thread is one conversation, so changing "Send from" on any step
 * changes it for all steps. Drafts on a different channel are converted: their
 * recipient swaps to the matching-channel option, email-only fields are cleared
 * when leaving email, and the LinkedIn-invite flag is dropped off-LinkedIn.
 */
export const applyDraftFromUpdate = (
	drafts: Draft[],
	option: SenderRecipientOption,
	recipientOptions: SenderRecipientOption[]
): Draft[] => {
	const matchingRecipient = recipientOptions.find((r) => r.type === option.type);

	return drafts.map((d) => ({
		...d,
		from: {
			userId: option.userId ?? 0,
			type: option.type,
			label: option.label,
			emailId: option.emailId,
			emailAddress: option.emailAddress,
		},
		...(d.to.type !== option.type && matchingRecipient
			? {
					to: {
						type: matchingRecipient.type,
						label: matchingRecipient.label,
						value: matchingRecipient.value ?? "",
					},
				}
			: {}),
		...clearedEmailFields(option.type),
		...inviteFlagFor(d, option.type),
	}));
};

/**
 * Recipient update: the chosen recipient applies to EVERY draft (one thread,
 * one destination). Drafts on a different channel move their sender to the
 * current user's own option on that channel (`pickMyOption`: my LinkedIn or my
 * default mailbox, never a colleague, never a disabled mailbox), or to "Select
 * sender" when I have none (PREC-4312); email-only fields are cleared when
 * leaving email and the LinkedIn-invite flag is dropped off-LinkedIn.
 */
export const applyDraftToUpdate = (
	drafts: Draft[],
	option: SenderRecipientOption,
	senderOptions: SenderRecipientOption[],
	me: User | undefined
): Draft[] => {
	const mine = pickMyOption(senderOptions, option.type, me);
	const from = mine
		? {
				userId: mine.userId ?? 0,
				type: mine.type,
				label: mine.label,
				emailId: mine.emailId,
				emailAddress: mine.emailAddress,
			}
		: { userId: 0, type: option.type, label: "Select sender" };

	return drafts.map((d) => ({
		...d,
		to: {
			type: option.type,
			label: option.label,
			value: option.value ?? "",
		},
		...(d.from.type !== option.type ? { from } : {}),
		...clearedEmailFields(option.type),
		...inviteFlagFor(d, option.type),
	}));
};
